import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

import { useGetProductsQuery } from "../../Store/api/apiSlice";

import styles from "../../styles/Category.module.scss";
import { assetUrl } from "../../utils/imageUrl";
import { applyPageSeo, resetPageSeo } from "../../utils/seo";

import Products from "../Products/Products";
import Preloader from "../Preloader/Preloader";

const PAGE_SIZE = 5;

const defaultValues = {
  title: "",
  price_min: "",
  price_max: "",
};

const buildQueryParams = (categoryId, filterValues, offset = 0) => {
  const params = {
    categoryId: Number(categoryId),
    limit: PAGE_SIZE,
    offset,
  };

  const title = filterValues.title?.trim();
  if (title) {
    params.search = title;
  }

  const priceMin = Number(filterValues.price_min);
  const priceMax = Number(filterValues.price_max);
  if (priceMin > 0) {
    params.price_min = priceMin;
  }
  if (priceMax > 0) {
    params.price_max = priceMax;
  }

  return params;
};

const Category = () => {
  const { id } = useParams();
  const { list } = useSelector(({ categories }) => categories);

  const [isEnd, setEnd] = useState(false);
  const [cat, setCat] = useState(null);
  const [items, setItems] = useState([]);
  const [values, setValues] = useState(defaultValues);
  const [params, setParams] = useState(() => buildQueryParams(id, defaultValues));

  const { data, isLoading, isFetching, isSuccess, isError } = useGetProductsQuery(params);
  const products = Array.isArray(data) ? data : [];

  useEffect(() => {
    if (!id) return;

    setValues(defaultValues);
    setItems([]);
    setEnd(false);
    setParams(buildQueryParams(id, defaultValues));
  }, [id]);

  useEffect(() => {
    if (!isSuccess || isLoading) return;

    if (!products.length) {
      if (params.offset === 0) {
        setItems([]);
      }
      setEnd(true);
      return;
    }

    setEnd(products.length < PAGE_SIZE);

    setItems((prevItems) => {
      if (params.offset === 0) {
        return products;
      }
      const newItems = products.filter(
        (item) => !prevItems.some((prev) => prev.id === item.id)
      );
      return [...prevItems, ...newItems];
    });
  }, [data, isSuccess, isLoading, params.offset]);

  useEffect(() => {
    if (!id || !list.length) return;

    const category = list.find((item) => item.id === id * 1);
    setCat(category);
  }, [list, id]);

  useEffect(() => {
    if (!cat) return;

    const robots = cat.metaRobots
      || (cat.seoIndex === false ? 'noindex, nofollow' : 'index, follow');

    applyPageSeo({
      title: cat.metaTitle || cat.name,
      description: cat.metaDescription || cat.name,
      keywords: cat.metaKeywords,
      canonical: cat.canonical || `/categories/${cat.id}`,
      ogImage: cat.image ? assetUrl(cat.image) : undefined,
      ogType: 'website',
      robots,
    });

    return resetPageSeo;
  }, [cat]);

  const handleChange = ({ target: { value, name } }) => {
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const applyFilters = (nextValues = values) => {
    const priceMin = Number(nextValues.price_min);
    const priceMax = Number(nextValues.price_max);

    if (priceMin > 0 && priceMax > 0 && priceMin > priceMax) {
      alert('Ціна «від» не може бути більшою за ціну «до»');
      return;
    }

    setItems([]);
    setEnd(false);
    setParams(buildQueryParams(id, nextValues, 0));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    applyFilters();
  };

  const handleReset = () => {
    setValues(defaultValues);
    setItems([]);
    setEnd(false);
    setParams(buildQueryParams(id, defaultValues, 0));
  };

  const handleLoadMore = () => {
    setParams((prev) => ({
      ...prev,
      offset: prev.offset + PAGE_SIZE,
    }));
  };

  const showEmpty = !isLoading && isSuccess && items.length === 0;
  const showError = isError && items.length === 0;

  if (cat && cat.isPublished === false) {
    return (
      <section className={styles.wrapper}>
        <div className={styles.back}>
          <span>Категорія тимчасово недоступна</span>
        </div>
      </section>
    );
  }

  return (
    <section className={styles.wrapper}>
      <h2 className={styles.title}>{cat?.h1Title || cat?.name}</h2>

      {cat?.descriptionBefore && (
        <div
          className={styles.categoryDescription}
          dangerouslySetInnerHTML={{ __html: cat.descriptionBefore }}
        />
      )}

      <form className={styles.filters} onSubmit={handleSubmit}>
        <div className={`${styles.filter} ${styles.filterGrow}`}>
          <input
            type="text"
            name="title"
            onChange={handleChange}
            placeholder="Найменування запчастини"
            value={values.title}
          />
        </div>
        <div className={styles.filter}>
          <input
            type="number"
            name="price_min"
            onChange={handleChange}
            placeholder="Від"
            min="0"
            value={values.price_min}
          />
          <span>Ціна від</span>
        </div>
        <div className={styles.filter}>
          <input
            type="number"
            name="price_max"
            onChange={handleChange}
            placeholder="До"
            min="0"
            value={values.price_max}
          />
          <span>Ціна до</span>
        </div>
        <div className={styles.filterActions}>
          <button type="submit" className={styles.applyButton}>
            Застосувати
          </button>
          <button type="button" className={styles.resetButton} onClick={handleReset}>
            Скинути
          </button>
        </div>
      </form>

      {isLoading && params.offset === 0 ? (
        <Preloader message="Завантаження каталогу…" />
      ) : showError ? (
        <div className={styles.back}>
          <span>Помилка завантаження товарів</span>
          <button type="button" onClick={handleReset}>Повторити</button>
        </div>
      ) : showEmpty ? (
        <div className={styles.back}>
          <span>Немає результату</span>
          <button type="button" onClick={handleReset}>Скинути фільтр</button>
        </div>
      ) : (
        <Products
          title=""
          products={items}
          style={{ padding: 0 }}
          amount={items.length}
        />
      )}

      {!isEnd && items.length > 0 && (
        <div className={styles.more}>
          <button type="button" onClick={handleLoadMore} disabled={isFetching}>
            {isFetching ? 'Завантаження…' : 'Ще товари'}
          </button>
        </div>
      )}

      {cat?.descriptionAfter && (
        <div
          className={styles.categoryDescription}
          dangerouslySetInnerHTML={{ __html: cat.descriptionAfter }}
        />
      )}
    </section>
  );
};

export default Category;
